import { useState } from "react";
import { businessRequest } from '@/lib/businessApi';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, LifeBuoy, Loader2, AlertTriangle, MessageSquare } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/site/Reveal";
import PolicyBody from "@/components/site/PolicyBody";
import { getPolicyBySlug } from "@/data/policyRegistry";
import { SITE } from "@/lib/site";
import useSeo from "@/hooks/useSeo";

const CATEGORIES = [
  "Account access", "Technical issue", "Privacy request", "Safety concern",
  "Report a user", "Professional verification", "Moderation appeal",
  "Child-safety concern", "Account deletion", "Copyright complaint", "General enquiry",
];

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  account_email: z.string().email("Enter a valid email").optional().or(z.literal("")),
  category: z.string().min(1, "Please choose a category"),
  subject: z.string().min(3, "Add a short subject"),
  description: z.string().min(10, "Please provide at least 10 characters"),
  privacy_ack: z.literal(true, { errorMap: () => ({ message: "Please acknowledge the privacy notice" }) }),
});

const faqs = [
  { q: "How do I get help with my account?", a: "Choose the most relevant category in the form and describe your issue. We'll reply to the email you provide, usually within 1–2 business days." },
  { q: "How does Orrbbit use my location?", a: "Location powers nearby discovery and is only accessed with your permission. During discovery, others see approximate proximity rather than your precise position. In People Mode, meetup location sharing can unlock between two users only after both accept a connection. See the Location & Radar Privacy Notice." },
  { q: "How do I report someone or something?", a: "You can report profiles and content directly in the app, or use this form and choose 'Report a user' or 'Safety concern'." },
  { q: "How do I delete my account?", a: "Use the in-app settings, or submit a logged-out request at /delete-account. We confirm by email using a secure single-use link." },
];

const field = "w-full rounded-xl border border-input bg-white px-4 py-3 text-navy placeholder:text-bodygrey/50 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/30";

export default function Support() {
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);
  const [emailSent,setEmailSent]=useState(false);
  const [supportSent,setSupportSent]=useState(false);
  const policy = getPolicyBySlug("support-complaints");
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({ resolver: zodResolver(schema) });

  useSeo({ title: "Support & Complaints — Orrbbit", description: "Get help from Orrbbit support. Contact us, browse FAQs, and read our Support & Complaints Policy.", path: "/support" });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = { ...data, account_email: data.account_email || null };
      const res = {data: await businessRequest('/support/contact', {method:'POST',token:null,body:payload})};
      setReference(res.data.reference);setEmailSent(!!res.data.email_sent);setSupportSent(!!res.data.support_email_sent);
      toast.success("Message sent", { description: `Reference ${res.data.reference}. ${res.data.email_sent ? "Check your inbox for a confirmation." : "Your request is saved. Email confirmation is temporarily unavailable."}` });
      reset();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error("Couldn't send message", { description: typeof detail === "string" ? detail : e.message || "Please try again or email support@orrbbit.com." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="support-page">
      <section className="border-b border-border bg-lightbg">
        <div className="container-x section-space">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-teal">We're here to help</span>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">Support</h1>
            <p className="mt-4 max-w-2xl text-lg text-bodygrey">Questions, bugs, privacy requests or safety concerns — reach our team and we'll help.</p>
          </Reveal>
        </div>
      </section>

      <div className="container-x py-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <a href={`mailto:${SITE.supportEmail}`} data-testid="support-email-link" className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5 shadow-soft hover:border-teal">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-teal/10 text-teal"><Mail size={20} /></span>
                <span><span className="block text-xs text-bodygrey">Email us</span><span className="block font-semibold text-navy">{SITE.supportEmail}</span></span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5 shadow-soft">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-orange/10 text-orange"><LifeBuoy size={20} /></span>
                <span><span className="block text-xs text-bodygrey">Response time</span><span className="block font-semibold text-navy">1–2 business days</span></span>
              </div>
            </Reveal>

            <Reveal className="mb-8">
              <div className="flex items-start gap-3 rounded-2xl border border-orange/30 bg-orange/5 p-5" data-testid="support-emergency-notice">
                <AlertTriangle className="mt-0.5 flex-none text-orange" size={20} />
                <p className="text-sm text-navy">Orrbbit support is not an emergency service. If you or someone else is in immediate danger, contact the appropriate local emergency service.</p>
              </div>
            </Reveal>

            {reference && (
              <Reveal className="mb-8">
                <div data-testid="support-success" className="rounded-2xl border border-teal/30 bg-teal/5 p-5 text-navy">
                  <p className="font-semibold">Thanks — your message has been received.</p><p className="mt-2 text-sm">{supportSent?"The support team has been notified by email.":"Your message is saved. The support email is queued for delivery."}</p>
                  <p className="mt-1 text-sm">Your reference number is <span className="font-mono font-semibold text-teal" data-testid="support-reference">{reference}</span>. {emailSent?"We’ve emailed you a confirmation.":"Your request is saved. Confirmation delivery is pending; keep this reference."}</p>
                </div>
              </Reveal>
            )}

            <Reveal>
              <form onSubmit={handleSubmit(onSubmit)} data-testid="support-form" className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="support-name-input" className="mb-1.5 block text-sm font-medium text-navy">Name</label>
                    <input id="support-name-input" data-testid="support-name-input" className={field} placeholder="Your name" {...register("name")} />
                    {errors.name && <p className="mt-1.5 text-xs text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="support-email-input" className="mb-1.5 block text-sm font-medium text-navy">Email</label>
                    <input id="support-email-input" data-testid="support-email-input" className={field} placeholder="you@email.com" {...register("email")} />
                    {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                </div>
                <div>
                  <label htmlFor="support-account-email-input" className="mb-1.5 block text-sm font-medium text-navy">Orrbbit account email <span className="text-bodygrey">(if different)</span></label>
                  <input id="support-account-email-input" data-testid="support-account-email-input" className={field} placeholder="Optional" {...register("account_email")} />
                  {errors.account_email && <p className="mt-1.5 text-xs text-red-500">{errors.account_email.message}</p>}
                </div>
                <div>
                  <label htmlFor="support-category" className="mb-1.5 block text-sm font-medium text-navy">Category</label>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field: f }) => (
                      <Select onValueChange={f.onChange} value={f.value}>
                        <SelectTrigger id="support-category" aria-label="Category" data-testid="support-category-select" className="h-[46px] rounded-xl border-input bg-white text-navy"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g,'-')}`}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.category && <p className="mt-1.5 text-xs text-red-500">{errors.category.message}</p>}
                </div>
                <div>
                  <label htmlFor="support-subject-input" className="mb-1.5 block text-sm font-medium text-navy">Subject</label>
                  <input id="support-subject-input" data-testid="support-subject-input" className={field} placeholder="What's this about?" {...register("subject")} />
                  {errors.subject && <p className="mt-1.5 text-xs text-red-500">{errors.subject.message}</p>}
                </div>
                <div>
                  <label htmlFor="support-message-input" className="mb-1.5 block text-sm font-medium text-navy">Description</label>
                  <textarea id="support-message-input" data-testid="support-message-input" rows={5} className={`${field} resize-none`} placeholder="How can we help?" {...register("description")} />
                  {errors.description && <p className="mt-1.5 text-xs text-red-500">{errors.description.message}</p>}
                </div>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-lightbg p-4">
                  <input type="checkbox" data-testid="support-privacy-ack" className="mt-1 h-4 w-4 accent-teal" {...register("privacy_ack")} />
                  <span className="text-sm text-bodygrey">I understand my details will be used to respond to my request in line with the <a href="/privacy" className="font-medium text-teal hover:underline">Privacy Policy</a>.</span>
                </label>
                {errors.privacy_ack && <p className="-mt-3 text-xs text-red-500">{errors.privacy_ack.message}</p>}
                <button type="submit" disabled={loading} data-testid="support-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-3 font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <MessageSquare size={18} />}{loading ? "Sending..." : "Send message"}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-2xl font-semibold text-navy">Frequently asked</h2>
              <Accordion type="single" collapsible className="mt-4" data-testid="support-faq">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-border">
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-medium text-navy hover:text-teal hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-bodygrey">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>

        {policy && (
          <Reveal className="mt-16 border-t border-border pt-10">
            <details data-testid="support-policy-details" className="group">
              <summary className="flex cursor-pointer items-center justify-between rounded-2xl border border-border bg-lightbg p-5">
                <span>
                  <span className="block font-heading text-lg font-semibold text-navy">{policy.title}</span>
                  <span className="mt-1 block text-sm text-bodygrey">Version {policy.version} · Effective {policy.effective_date} · Last updated {policy.last_updated}</span>
                </span>
                <span className="text-sm font-semibold text-teal group-open:hidden">Read policy</span>
                <span className="hidden text-sm font-semibold text-teal group-open:inline">Hide</span>
              </summary>
              <div className="mt-6 max-w-3xl"><PolicyBody policy={policy} /></div>
            </details>
          </Reveal>
        )}
      </div>
    </div>
  );
}

